import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, Search, MoreHorizontal, ClipboardList, Calendar, User, AlertCircle, Eye, Play, CheckCircle, Trash2, QrCode, Scan, Package, Clock, Download, Upload } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { solicitudesService } from '../../services/solicitudes';
import { activosService } from '../../services/activos';
import { fotosService } from '../../services/fotos';
import { useAuth } from '../../hooks/useAuth';
import Button from '../common/Button';
import Modal from '../common/Modal';
import QRScanner from '../common/QRScanner';

// Deduce el tipo de solicitud a partir del prefijo de su código:
// SST-/CCTV-/CIG- se generan automáticamente desde un checklist de inspección;
// cualquier otro código (normalmente SOL-...) es una solicitud manual.
const TIPO_OPTIONS = [
    { value: 'todos', label: 'Todos los tipos' },
    { value: 'SOL', label: 'Solicitud manual (SOL)' },
    { value: 'SST', label: 'Inspección SST' },
    { value: 'CIG', label: 'Inspección CI Generales' },
    { value: 'CCTV', label: 'Inspección CCTV' },
];

const getTipoFromCodigo = (codigo = '') => {
    if (codigo.startsWith('SST-')) return 'SST';
    if (codigo.startsWith('CIG-')) return 'CIG';
    if (codigo.startsWith('CCTV-')) return 'CCTV';
    return 'SOL';
};

const PRIORIDAD_OPTIONS = [
    { value: 'todas', label: 'Todas las prioridades' },
    { value: 'urgente', label: 'Urgente' },
    { value: 'alta', label: 'Alta' },
    { value: 'media', label: 'Media' },
    { value: 'baja', label: 'Baja' },
];

const SolicitudesPage = () => {
    const { user, isAdmin } = useAuth();
    const [kanbanData, setKanbanData] = useState({
        pendientes: [],
        en_ejecucion: [],
        finalizadas: [],
        stats: { total: 0, total_pendientes: 0, total_en_ejecucion: 0, total_finalizadas: 0 }
    });
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showScannerModal, setShowScannerModal] = useState(false);
    const [selectedSolicitud, setSelectedSolicitud] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [activos, setActivos] = useState([]);
    const [tipoFilter, setTipoFilter] = useState('todos');
    const [prioridadFilter, setPrioridadFilter] = useState('todas');
    const [ubicacionFilter, setUbicacionFilter] = useState('todas');
    const [showImportModal, setShowImportModal] = useState(false);
    const [importing, setImporting] = useState(false);
    const fileInputRef = useRef(null);

    // Cargar datos del Kanban
    const loadKanbanData = async () => {
        try {
            setLoading(true);
            const data = await solicitudesService.getKanban();
            console.log('Datos de solicitudes:', data);
            setKanbanData(data);
        } catch (error) {
            toast.error('Error al cargar solicitudes');
            console.error('Error loading kanban data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Cargar activos para el formulario
    const loadActivos = async () => {
        try {
            const data = await activosService.getAll({ limit: 1000 });
            // El backend devuelve { items: [...], total: ... } o un array directo
            const activosList = Array.isArray(data) ? data : (data.items || []);
            setActivos(activosList);
        } catch (error) {
            console.error('Error loading activos:', error);
            setActivos([]);
        }
    };

    useEffect(() => {
        loadKanbanData();
        loadActivos();
    }, []);

    // Buscar solicitudes
    const handleSearch = async () => {
        if (searchTerm.trim()) {
            try {
                const results = await solicitudesService.getAll({ search: searchTerm });
                const grouped = {
                    pendientes: results.filter(s => s.estado === 'pendiente'),
                    en_ejecucion: results.filter(s => s.estado === 'en_ejecucion'),
                    finalizadas: results.filter(s => s.estado === 'finalizada'),
                    stats: kanbanData.stats
                };
                setKanbanData(grouped);
            } catch (error) {
                toast.error('Error en la búsqueda');
            }
        } else {
            loadKanbanData();
        }
    };

    useEffect(() => {
        const delayedSearch = setTimeout(() => {
            handleSearch();
        }, 500);
        return () => clearTimeout(delayedSearch);
    }, [searchTerm]);

    const handleCreateSolicitud = async (solicitudData) => {
        try {
            await solicitudesService.create(solicitudData);
            toast.success('Solicitud creada correctamente');
            setShowCreateModal(false);
            loadKanbanData();
        } catch (error) {
            toast.error('Error al crear solicitud');
        }
    };

    const handleChangeStatus = async (solicitudId, newStatus) => {
        try {
            await solicitudesService.cambiarEstado(solicitudId, newStatus);
            toast.success('Estado actualizado correctamente');
            loadKanbanData();
        } catch (error) {
            toast.error('Error al cambiar estado');
        }
    };

    const handleDeleteSolicitud = async (solicitudId) => {
        if (window.confirm('¿Estás seguro de eliminar esta solicitud?')) {
            try {
                await solicitudesService.delete(solicitudId);
                toast.success('Solicitud eliminada correctamente');
                loadKanbanData();
            } catch (error) {
                toast.error('Error al eliminar solicitud');
            }
        }
    };

    const handleSolicitudClick = async (solicitud) => {
        try {
            const detailedSolicitud = await solicitudesService.getById(solicitud.id);
            setSelectedSolicitud(detailedSolicitud);
            setShowDetailModal(true);
        } catch (error) {
            toast.error('Error al cargar detalles de la solicitud');
        }
    };

    // Manejar escaneo de código
    const handleScan = async (scannedCode) => {
        // Primero cerrar el scanner
        setShowScannerModal(false);

        // Esperar a que el scanner se cierre completamente antes de abrir otro modal
        await new Promise(resolve => setTimeout(resolve, 300));

        try {
            // Intentar buscar el activo por código escaneado
            const activo = await solicitudesService.scanBarcode(scannedCode);

            if (activo) {
                // Abrir modal de creación con activo preseleccionado
                setSelectedSolicitud({ preselectedActivo: activo });
                setShowCreateModal(true);
                toast.success(`Activo ${activo.code} encontrado`);
            }
        } catch (error) {
            // Si no se encuentra por código de barras, intentar buscar en activos normales
            try {
                const activoEncontrado = activos.find(a =>
                    a.code === scannedCode ||
                    a.urlcodeqr?.includes(scannedCode)
                );

                if (activoEncontrado) {
                    setSelectedSolicitud({ preselectedActivo: activoEncontrado });
                    setShowCreateModal(true);
                    toast.success(`Activo ${activoEncontrado.code} encontrado`);
                } else {
                    toast.error('No se encontró ningún activo con ese código');
                }
            } catch (searchError) {
                toast.error('Código no reconocido');
            }
        }
    };

    // Exportar a Excel (todas las solicitudes visibles para el usuario actual)
    const handleExport = async () => {
        try {
            await solicitudesService.exportarExcel(false);
            toast.success('Archivo descargado correctamente');
        } catch (error) {
            toast.error('Error al exportar el archivo');
        }
    };

    // Importar solicitudes desde un archivo Excel
    const handleImportFile = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            setImporting(true);
            const result = await solicitudesService.importarExcel(file);
            toast.success(result.message);
            if (result.errors?.length) {
                console.warn('Errores al importar solicitudes:', result.errors);
                toast.error(`${result.errors.length} fila(s) con errores. Revisa la consola.`);
            }
            setShowImportModal(false);
            loadKanbanData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al importar el archivo');
        } finally {
            setImporting(false);
            event.target.value = '';
        }
    };

    // Ubicaciones únicas presentes en las solicitudes actuales, para poblar el filtro
    const ubicacionesDisponibles = useMemo(() => {
        const todas = [
            ...kanbanData.pendientes,
            ...kanbanData.en_ejecucion,
            ...kanbanData.finalizadas,
        ]
            .map((s) => s.ubicacion)
            .filter((u) => !!u && u.trim() !== '');
        return Array.from(new Set(todas)).sort((a, b) => a.localeCompare(b));
    }, [kanbanData]);

    // Aplica los filtros de tipo, prioridad y ubicación (por encima de la búsqueda por texto) a cada columna
    const filteredKanbanData = useMemo(() => {
        const aplicarFiltros = (lista) => lista.filter((s) => {
            const pasaTipo = tipoFilter === 'todos' || getTipoFromCodigo(s.codigo) === tipoFilter;
            const pasaPrioridad = prioridadFilter === 'todas' || s.prioridad === prioridadFilter;
            const pasaUbicacion = ubicacionFilter === 'todas' || s.ubicacion === ubicacionFilter;
            return pasaTipo && pasaPrioridad && pasaUbicacion;
        });

        return {
            ...kanbanData,
            pendientes: aplicarFiltros(kanbanData.pendientes),
            en_ejecucion: aplicarFiltros(kanbanData.en_ejecucion),
            finalizadas: aplicarFiltros(kanbanData.finalizadas),
        };
    }, [kanbanData, tipoFilter, prioridadFilter, ubicacionFilter]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                        Solicitudes de Trabajo
                        {!isAdmin() && <span className="text-sm font-normal text-gray-600 ml-2">(Mis solicitudes)</span>}
                    </h1>
                    <p className="text-gray-600">
                        {isAdmin() ? 'Gestión de todas las solicitudes del sistema' : 'Mis solicitudes de mantenimiento'}
                    </p>
                </div>
                <div className="flex space-x-3">
                    <button
                        onClick={handleExport}
                        className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                    >
                        <Download className="h-4 w-4" />
                        Exportar Excel
                    </button>
                    <button
                        onClick={() => setShowImportModal(true)}
                        className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                    >
                        <Upload className="h-4 w-4" />
                        Importar Excel
                    </button>
                    <button
                        onClick={() => setShowScannerModal(true)}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                    >
                        <Scan className="h-4 w-4" />
                        Escanear Código
                    </button>
                    <button
                        onClick={() => {
                            setSelectedSolicitud(null);
                            setShowCreateModal(true);
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                    >
                        <Plus className="h-4 w-4" />
                        Nueva Solicitud
                    </button>
                </div>
            </div>

            {/* Search Bar + Filtros */}
            <div className="bg-white p-4 rounded-lg shadow-md space-y-4">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <input
                        type="text"
                        placeholder="Buscar solicitudes..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                </div>
                <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4">
                    <select
                        value={tipoFilter}
                        onChange={(e) => setTipoFilter(e.target.value)}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        {TIPO_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                    <select
                        value={prioridadFilter}
                        onChange={(e) => setPrioridadFilter(e.target.value)}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        {PRIORIDAD_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                    <select
                        value={ubicacionFilter}
                        onChange={(e) => setUbicacionFilter(e.target.value)}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value="todas">Todas las ubicaciones</option>
                        {ubicacionesDisponibles.map((ubicacion) => (
                            <option key={ubicacion} value={ubicacion}>{ubicacion}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Stats Cards - Estados */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Total</p>
                            <p className="text-2xl font-bold text-gray-900">{kanbanData.stats.total}</p>
                        </div>
                        <ClipboardList className="h-8 w-8 text-gray-400" />
                    </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Pendientes</p>
                            <p className="text-2xl font-bold text-yellow-600">{kanbanData.stats.total_pendientes}</p>
                        </div>
                        <AlertCircle className="h-8 w-8 text-yellow-400" />
                    </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">En Ejecución</p>
                            <p className="text-2xl font-bold text-blue-600">{kanbanData.stats.total_en_ejecucion}</p>
                        </div>
                        <Play className="h-8 w-8 text-blue-400" />
                    </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Finalizadas</p>
                            <p className="text-2xl font-bold text-green-600">{kanbanData.stats.total_finalizadas}</p>
                        </div>
                        <CheckCircle className="h-8 w-8 text-green-400" />
                    </div>
                </div>
            </div>

            {/* Stats Cards - Prioridades */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-red-600 font-medium">Urgentes</p>
                            <p className="text-2xl font-bold text-red-700">
                                {[...filteredKanbanData.pendientes, ...filteredKanbanData.en_ejecucion].filter(s => s.prioridad === 'urgente').length}
                            </p>
                        </div>
                        <div className="relative">
                            <AlertCircle className="h-8 w-8 text-red-500" />
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                        </div>
                    </div>
                </div>
                <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-orange-600 font-medium">Alta Prioridad</p>
                            <p className="text-2xl font-bold text-orange-700">
                                {[...filteredKanbanData.pendientes, ...filteredKanbanData.en_ejecucion].filter(s => s.prioridad === 'alta').length}
                            </p>
                        </div>
                        <AlertCircle className="h-8 w-8 text-orange-500" />
                    </div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-yellow-600 font-medium">Media Prioridad</p>
                            <p className="text-2xl font-bold text-yellow-700">
                                {[...filteredKanbanData.pendientes, ...filteredKanbanData.en_ejecucion].filter(s => s.prioridad === 'media').length}
                            </p>
                        </div>
                        <Clock className="h-8 w-8 text-yellow-500" />
                    </div>
                </div>
            </div>

            {/* Kanban Board */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <KanbanColumn
                    title="Pendiente"
                    solicitudes={filteredKanbanData.pendientes}
                    onSolicitudClick={handleSolicitudClick}
                    onChangeStatus={handleChangeStatus}
                    onDelete={handleDeleteSolicitud}
                    status="pendiente"
                    color="yellow"
                    isAdmin={isAdmin()}
                />
                <KanbanColumn
                    title="En Ejecución"
                    solicitudes={filteredKanbanData.en_ejecucion}
                    onSolicitudClick={handleSolicitudClick}
                    onChangeStatus={handleChangeStatus}
                    onDelete={handleDeleteSolicitud}
                    status="en_ejecucion"
                    color="blue"
                    isAdmin={isAdmin()}
                />
                <KanbanColumn
                    title="Finalizada"
                    solicitudes={filteredKanbanData.finalizadas}
                    onSolicitudClick={handleSolicitudClick}
                    onChangeStatus={handleChangeStatus}
                    onDelete={handleDeleteSolicitud}
                    status="finalizada"
                    color="green"
                    isAdmin={isAdmin()}
                />
            </div>

            {/* Modal de Crear Solicitud */}
            {showCreateModal && (
                <Modal
                    title="Nueva Solicitud"
                    onClose={() => {
                        setShowCreateModal(false);
                        setSelectedSolicitud(null);
                    }}
                    size="lg"
                >
                    <CreateSolicitudModal
                        onClose={() => {
                            setShowCreateModal(false);
                            setSelectedSolicitud(null);
                        }}
                        onSubmit={handleCreateSolicitud}
                        activos={activos}
                        preselectedActivo={selectedSolicitud?.preselectedActivo}
                        user={user}
                    />
                </Modal>
            )}

            {/* Modal de Detalles */}
            {showDetailModal && selectedSolicitud && (
                <Modal
                    title={`Solicitud - ${selectedSolicitud.codigo}`}
                    onClose={() => {
                        setShowDetailModal(false);
                        setSelectedSolicitud(null);
                    }}
                    size="lg"
                >
                    <SolicitudDetailModal
                        solicitud={selectedSolicitud}
                        onClose={() => {
                            setShowDetailModal(false);
                            setSelectedSolicitud(null);
                        }}
                        onChangeStatus={handleChangeStatus}
                        onDelete={handleDeleteSolicitud}
                        isAdmin={isAdmin()}
                    />
                </Modal>
            )}

            {/* Modal de Importar Excel */}
            {showImportModal && (
                <Modal
                    title="Importar Solicitudes desde Excel"
                    onClose={() => setShowImportModal(false)}
                    size="lg"
                >
                    <div className="space-y-4">
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="flex items-start space-x-3">
                                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                                <div className="text-sm text-yellow-800">
                                    <p className="font-medium mb-1">Columnas del archivo:</p>
                                    <p><strong>Requerida:</strong> descripcion</p>
                                    <p><strong>Opcionales:</strong> codigo, prioridad (baja/media/alta/urgente), ubicacion, solicitado_por, notas, tiempo_ejecucion</p>
                                </div>
                            </div>
                        </div>

                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                            <Upload className="mx-auto h-10 w-10 text-gray-400" />
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xlsx,.xls"
                                onChange={handleImportFile}
                                className="hidden"
                            />
                            <Button
                                onClick={() => fileInputRef.current?.click()}
                                variant="outline"
                                className="mt-3"
                                loading={importing}
                            >
                                Seleccionar Archivo Excel
                            </Button>
                            <p className="text-xs text-gray-500 mt-2">Formatos: .xlsx, .xls</p>
                        </div>

                        <div className="flex justify-end pt-2 border-t">
                            <Button onClick={() => setShowImportModal(false)} variant="outline">
                                Cerrar
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Modal del Escáner */}
            {showScannerModal && (
                <QRScanner
                    isOpen={showScannerModal}
                    onScan={handleScan}
                    onClose={() => setShowScannerModal(false)}
                />
            )}
        </div>
    );
};

// Componente para cada columna del Kanban
const KanbanColumn = ({ title, solicitudes, onSolicitudClick, onChangeStatus, onDelete, status, color, isAdmin }) => {
    const getHeaderColor = () => {
        switch (color) {
            case 'yellow': return 'bg-yellow-100 border-yellow-300';
            case 'blue': return 'bg-blue-100 border-blue-300';
            case 'green': return 'bg-green-100 border-green-300';
            default: return 'bg-gray-100 border-gray-300';
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-md">
            <div className={`px-4 py-3 border-b-2 ${getHeaderColor()}`}>
                <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">{title}</h3>
                    <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded-full text-xs font-medium">
                        {solicitudes.length}
                    </span>
                </div>
            </div>

            <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
                {solicitudes.map((solicitud) => (
                    <SolicitudCard
                        key={solicitud.id}
                        solicitud={solicitud}
                        onClick={() => onSolicitudClick(solicitud)}
                        onChangeStatus={onChangeStatus}
                        onDelete={onDelete}
                        currentStatus={status}
                        isAdmin={isAdmin}
                    />
                ))}

                {solicitudes.length === 0 && (
                    <div className="text-center py-8">
                        <ClipboardList className="mx-auto h-12 w-12 text-gray-400" />
                        <p className="mt-2 text-sm text-gray-500">No hay solicitudes</p>
                    </div>
                )}
            </div>
        </div>
    );
};

// Componente para cada tarjeta de solicitud
const SolicitudCard = ({ solicitud, onClick, onChangeStatus, onDelete, currentStatus, isAdmin }) => {
    const [showMenu, setShowMenu] = useState(false);

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'alta': return 'bg-red-100 text-red-800';
            case 'media': return 'bg-yellow-100 text-yellow-800';
            case 'baja': return 'bg-green-100 text-green-800';
            case 'urgente': return 'bg-purple-100 text-purple-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'No definida';
        return new Date(dateString).toLocaleDateString('es-ES');
    };

    const getNextStatus = () => {
        switch (currentStatus) {
            case 'pendiente': return 'en_ejecucion';
            case 'en_ejecucion': return 'finalizada';
            default: return null;
        }
    };

    const getStatusAction = () => {
        switch (currentStatus) {
            case 'pendiente': return 'Iniciar';
            case 'en_ejecucion': return 'Finalizar';
            default: return null;
        }
    };

    return (
        <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer relative">
            <div onClick={onClick}>
                {/* Header de la tarjeta */}
                <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 text-sm">{solicitud.codigo}</h4>
                        <p className="text-xs text-gray-600">
                            Activo: {solicitud.activo?.code || 'Sin activo'} - {solicitud.activo?.name || 'N/A'}
                        </p>
                    </div>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowMenu(!showMenu);
                        }}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <MoreHorizontal className="h-4 w-4" />
                    </button>
                </div>

                {/* Descripción */}
                <p className="text-sm font-medium text-gray-900 mb-2 line-clamp-2">{solicitud.descripcion}</p>

                {/* Información del activo */}
                <div className="mb-3">
                    <p className="text-xs text-gray-600">Ubicación: {solicitud.activo?.ubicado || 'Sin ubicación'}</p>
                    <p className="text-xs text-gray-600">Tiempo est.: {solicitud.tiempo_ejecucion || 1}h</p>
                </div>

                {/* Fecha de solicitud */}
                <div className="mb-3">
                    <p className="text-xs text-gray-600">
                        Fecha: {formatDate(solicitud.fecha_solicitud || solicitud.fecha_creacion)}
                    </p>
                </div>

                {/* Solicitante */}
                <div className="flex items-center text-xs text-gray-600 mb-3">
                    <User className="h-3 w-3 mr-1" />
                    <span>{solicitud.solicitado_por || 'Sin asignar'}</span>
                </div>

                {/* Prioridad */}
                {solicitud.prioridad && (
                    <div className="mb-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(solicitud.prioridad)}`}>
                            {solicitud.prioridad?.charAt(0).toUpperCase() + solicitud.prioridad?.slice(1)}
                        </span>
                    </div>
                )}
            </div>

            {/* Menu desplegable */}
            {showMenu && (
                <div className="absolute top-8 right-4 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-32">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onClick();
                            setShowMenu(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                        <Eye className="h-3 w-3" />
                        Ver detalles
                    </button>

                    {(isAdmin || getNextStatus()) && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onChangeStatus(solicitud.id, getNextStatus());
                                setShowMenu(false);
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-blue-700 hover:bg-blue-50 flex items-center gap-2"
                        >
                            <Play className="h-3 w-3" />
                            {getStatusAction()}
                        </button>
                    )}

                    {isAdmin && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete(solicitud.id);
                                setShowMenu(false);
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                        >
                            <Trash2 className="h-3 w-3" />
                            Eliminar
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

// Modal para crear nueva solicitud
const CreateSolicitudModal = ({ onClose, onSubmit, activos, preselectedActivo, user }) => {
    const [formData, setFormData] = useState({
        codigo: '',
        descripcion: '',
        activo_id: preselectedActivo?.id || '',
        prioridad: 'media',
        solicitado_por: user?.full_name || '',
        tiempo_ejecucion: 2, // Tiempo por defecto para prioridad media
        notas: '',
        ubicacion: preselectedActivo?.ubicacion_obj?.nombre || ''
    });

    // Generar código automático al cargar el componente
    useEffect(() => {
        const generateCode = () => {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            
            return `SOL-${year}${month}${day}-${hours}${minutes}`;
        };

        setFormData(prev => ({
            ...prev,
            codigo: generateCode()
        }));
    }, []);

    useEffect(() => {
        if (preselectedActivo) {
            setFormData(prev => ({
                ...prev,
                activo_id: preselectedActivo.id,
                ubicacion: preselectedActivo.ubicacion_obj?.nombre || 'Sin ubicación',
                descripcion: `Solicitud para ${preselectedActivo.name}`
            }));
        }
    }, [preselectedActivo]);

    // Auto-llenar tiempo estimado según prioridad
    useEffect(() => {
        const tiemposPorPrioridad = {
            'baja': 4,
            'media': 2,
            'alta': 1,
            'urgente': 0.5
        };

        setFormData(prev => ({
            ...prev,
            tiempo_ejecucion: tiemposPorPrioridad[prev.prioridad] || 2
        }));
    }, [formData.prioridad]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.codigo.trim()) {
            toast.error('El código es requerido');
            return;
        }
        if (!formData.descripcion.trim()) {
            toast.error('La descripción es requerida');
            return;
        }

        const submitData = {
            ...formData,
            activo_id: formData.activo_id ? parseInt(formData.activo_id) : null,
            tiempo_ejecucion: parseInt(formData.tiempo_ejecucion) || 1
        };

        onSubmit(submitData);
    };

    const selectedActivo = activos.find(a => a.id === parseInt(formData.activo_id));

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {preselectedActivo && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center space-x-3">
                        <Package className="h-6 w-6 text-green-600" />
                        <div>
                            <h3 className="font-semibold text-green-900">Activo escaneado</h3>
                            <p className="text-green-800">{preselectedActivo.code} - {preselectedActivo.name}</p>
                            <p className="text-sm text-green-600">Ubicación: {preselectedActivo.ubicacion_obj?.nombre || 'Sin ubicación'}</p>
                        </div>
                    </div>
                </div>
            )}

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Código de Solicitud *
                    <span className="text-xs text-gray-500 ml-1">(Generado automáticamente)</span>
                </label>
                <input
                    type="text"
                    value={formData.codigo}
                    readOnly
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 cursor-not-allowed"
                    placeholder="SOL-001"
                    required
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción *
                </label>
                <textarea
                    value={formData.descripcion}
                    onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    rows={3}
                    placeholder="Describe el problema o solicitud..."
                    required
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Activo
                </label>
                <select
                    value={formData.activo_id}
                    onChange={(e) => {
                        const activoId = e.target.value;
                        const activo = activos.find(a => a.id === parseInt(activoId));
                        setFormData({
                            ...formData, 
                            activo_id: activoId,
                            ubicacion: activo ? (activo.ubicacion_obj?.nombre || 'Sin ubicación') : ''
                        });
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                    <option value="">Seleccionar activo (opcional)</option>
                    {activos.map((activo) => (
                        <option key={activo.id} value={activo.id}>
                            {activo.code} - {activo.name}
                        </option>
                    ))}
                </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Prioridad
                    </label>
                    <select
                        value={formData.prioridad}
                        onChange={(e) => setFormData({...formData, prioridad: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    >
                        <option value="baja">Baja</option>
                        <option value="media">Media</option>
                        <option value="alta">Alta</option>
                        <option value="urgente">Urgente</option>
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tiempo Estimado (horas)
                        <span className="text-xs text-gray-500 ml-1">(Auto-calculado por prioridad)</span>
                    </label>
                    <input
                        type="number"
                        value={formData.tiempo_ejecucion}
                        onChange={(e) => setFormData({...formData, tiempo_ejecucion: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50"
                        min="0.5"
                        step="0.5"
                        placeholder="Auto-calculado"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Urgente: 0.5h | Alta: 1h | Media: 2h | Baja: 4h
                    </p>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Solicitado por
                </label>
                <input
                    type="text"
                    value={formData.solicitado_por}
                    onChange={(e) => setFormData({...formData, solicitado_por: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="Nombre del solicitante"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ubicación
                </label>
                <input
                    type="text"
                    value={formData.ubicacion}
                    onChange={(e) => setFormData({...formData, ubicacion: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="Ubicación del trabajo"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notas adicionales
                </label>
                <textarea
                    value={formData.notas}
                    onChange={(e) => setFormData({...formData, notas: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    rows={2}
                    placeholder="Información adicional..."
                />
            </div>

            {selectedActivo && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">Información del Activo Seleccionado</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm text-blue-800">
                        <div>Fabricante: {selectedActivo.factory}</div>
                        <div>Modelo: {selectedActivo.model}</div>
                        <div>Ubicación: {selectedActivo.ubicacion_obj?.nombre || 'Sin ubicación'}</div>
                        <div>Estado: {selectedActivo.outservice ? 'Fuera de servicio' : 'En servicio'}</div>
                    </div>
                </div>
            )}

            <div className="flex gap-3 pt-4">
                <Button type="button" onClick={onClose} variant="outline" className="flex-1">
                    Cancelar
                </Button>
                <Button type="submit" className="flex-1">
                    Crear Solicitud
                </Button>
            </div>
        </form>
    );
};

// Modal para ver detalles de solicitud
const SolicitudDetailModal = ({ solicitud, onClose, onChangeStatus, onDelete, isAdmin }) => {
    const [fotos, setFotos] = useState([]);

    useEffect(() => {
        if (!solicitud?.id) return;
        fotosService.listarPorSolicitud(solicitud.id)
            .then(setFotos)
            .catch(() => setFotos([]));
    }, [solicitud?.id]);

    const formatDate = (dateString) => {
        if (!dateString) return 'No definida';
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pendiente': return 'bg-yellow-100 text-yellow-800';
            case 'en_ejecucion': return 'bg-blue-100 text-blue-800';
            case 'finalizada': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'alta': return 'bg-red-100 text-red-800';
            case 'media': return 'bg-yellow-100 text-yellow-800';
            case 'baja': return 'bg-green-100 text-green-800';
            case 'urgente': return 'bg-purple-100 text-purple-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getNextStatus = () => {
        switch (solicitud.estado) {
            case 'pendiente': return 'en_ejecucion';
            case 'en_ejecucion': return 'finalizada';
            default: return null;
        }
    };

    const getStatusAction = () => {
        switch (solicitud.estado) {
            case 'pendiente': return 'Iniciar Ejecución';
            case 'en_ejecucion': return 'Marcar como Finalizada';
            default: return null;
        }
    };

    return (
        <div className="space-y-6">
            {/* Header con estado */}
            <div className="flex items-center justify-between border-b pb-4">
                <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(solicitud.estado)}`}>
                        {solicitud.estado?.charAt(0).toUpperCase() + solicitud.estado?.slice(1).replace('_', ' ')}
                    </span>
                    {solicitud.prioridad && (
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(solicitud.prioridad)}`}>
                            Prioridad {solicitud.prioridad?.charAt(0).toUpperCase() + solicitud.prioridad?.slice(1)}
                        </span>
                    )}
                </div>
            </div>

            {/* Información básica */}
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Información de la Solicitud</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div>
                        <span className="font-medium text-gray-700">Código:</span>
                        <p className="text-gray-900 mt-1">{solicitud.codigo}</p>
                    </div>
                    <div>
                        <span className="font-medium text-gray-700">Descripción:</span>
                        <p className="text-gray-900 mt-1">{solicitud.descripcion}</p>
                    </div>
                    {solicitud.notas && (
                        <div>
                            <span className="font-medium text-gray-700">Notas:</span>
                            <p className="text-gray-900 mt-1">{solicitud.notas}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Fotos adjuntas (del hallazgo de checklist que originó la solicitud) */}
            {fotos.length > 0 && (
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Fotos Adjuntas</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {fotos.map((foto) => (
                            <a key={foto.id} href={foto.url} target="_blank" rel="noopener noreferrer">
                                <img
                                    src={foto.url}
                                    alt="Foto de la solicitud"
                                    className="w-full h-28 object-cover rounded-lg border border-gray-200 hover:opacity-80 transition-opacity"
                                />
                            </a>
                        ))}
                    </div>
                </div>
            )}

            {/* Información del Activo */}
            {solicitud.activo && (
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Activo Relacionado</h3>
                    <div className="bg-blue-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <span className="font-medium text-gray-700">Código:</span>
                                <p className="text-gray-900">{solicitud.activo.code}</p>
                            </div>
                            <div>
                                <span className="font-medium text-gray-700">Nombre:</span>
                                <p className="text-gray-900">{solicitud.activo.name}</p>
                            </div>
                            <div>
                                <span className="font-medium text-gray-700">Fabricante:</span>
                                <p className="text-gray-900">{solicitud.activo.factory}</p>
                            </div>
                            <div>
                                <span className="font-medium text-gray-700">Modelo:</span>
                                <p className="text-gray-900">{solicitud.activo.model}</p>
                            </div>
                            <div className="md:col-span-2">
                                <span className="font-medium text-gray-700">Ubicación:</span>
                                <p className="text-gray-900">{solicitud.activo.ubicado}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Detalles de trabajo */}
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Detalles del Trabajo</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <span className="font-medium text-gray-700">Tiempo estimado:</span>
                            <p className="text-gray-900">{solicitud.tiempo_ejecucion || 1} horas</p>
                        </div>
                        <div>
                            <span className="font-medium text-gray-700">Solicitado por:</span>
                            <p className="text-gray-900">{solicitud.solicitado_por || 'No especificado'}</p>
                        </div>
                        <div>
                            <span className="font-medium text-gray-700">Ubicación:</span>
                            <p className="text-gray-900">{solicitud.ubicacion || 'No especificada'}</p>
                        </div>
                        <div>
                            <span className="font-medium text-gray-700">Fecha de solicitud:</span>
                            <p className="text-gray-900">{formatDate(solicitud.fecha_solicitud || solicitud.fecha_creacion)}</p>
                        </div>
                        {solicitud.fecha_incidente && (
                            <div>
                                <span className="font-medium text-gray-700">Fecha del incidente:</span>
                                <p className="text-gray-900">{formatDate(solicitud.fecha_incidente)}</p>
                            </div>
                        )}
                        {solicitud.fecha_termino && (
                            <div>
                                <span className="font-medium text-gray-700">Fecha de término:</span>
                                <p className="text-gray-900">{formatDate(solicitud.fecha_termino)}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Método de solución (si existe) */}
            {solicitud.metodo_solucion && (
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Método de Solución</h3>
                    <div className="bg-green-50 rounded-lg p-4">
                        <p className="text-gray-900">{solicitud.metodo_solucion}</p>
                    </div>
                </div>
            )}

            {/* Acciones */}
            <div className="flex gap-3 pt-6 border-t">
                <Button onClick={onClose} variant="outline" className="flex-1">
                    Cerrar
                </Button>

                {(isAdmin || getNextStatus()) && getNextStatus() && (
                    <Button
                        onClick={() => {
                            onChangeStatus(solicitud.id, getNextStatus());
                            onClose();
                        }}
                        className="flex-1"
                    >
                        {getStatusAction()}
                    </Button>
                )}

                {isAdmin && (
                    <Button
                        onClick={() => {
                            onDelete(solicitud.id);
                            onClose();
                        }}
                        variant="danger"
                        className="bg-red-600 hover:bg-red-700 text-white"
                    >
                        Eliminar
                    </Button>
                )}
            </div>
        </div>
    );
};

export default SolicitudesPage;